import { useCallback, useMemo, useState } from 'react';
import { useMediaQuery } from '@/shared/hooks/use-media-query';
import { useTranslation } from 'react-i18next';
import { agencyContext } from '@pages/authenticated/agency-pages/AgenciesContext';
import { useIsMobile } from '@/shared/hooks/use-is-mobile';
import { useIsSoloBroker } from '@/shared/hooks/use-is-solo-broker';
import { useToast } from '@/shared/hooks/use-toast';
import { Text, UnstyledButton } from '@/shared/ui';
import { formatCents } from '@/shared/utils/format-cents';
import { formatLocalizedDate } from '@/shared/utils/date';
import { FF_APP_ADDONS } from '@/shared/utils/feature-flag';
import { buildMeters, decideOffer } from '@/shared/utils/plan-tab-model';
import { findUpgradeTarget } from '../components/subscription-usage.helpers';
import { resolvePlanName } from '../components/plan-display-name';
import { usePlanTabData } from '../hooks/usePlanTabData';
import { useAgencyTeam } from '../hooks/useAgencyTeam';
import SupportSeatsMeter from '../components/seats/SupportSeatsMeter';
import { SeatsBlock } from '../components/seats/SeatsBlock';
import { PlanSummary } from '../components/plan-v2/PlanSummary';
import { SeatUnlocks } from '../components/plan-v2/SeatUnlocks';
import { UpgradePush } from '../components/plan-v2/UpgradePush';
import { BillingDates } from '../components/plan-v2/BillingDates';
import { lastCharge, nextCharge } from '../components/plan-v2/billing-dates';
import {
  cycleTotalCents, nextChargeCents, seatCount, seatsAtRenewal,
} from '../components/plan-v2/plan-summary';
import {
  mostUnderPressure, onLadder, seatUnlocks, upgradeOffer,
} from '../components/plan-v2/seat-unlocks';
import { isPlanSelectable } from '@features/onboarding/hooks/use-plans';
import { useSubscriptionAddons } from '../hooks/useSubscriptionAddons';
import { AllInvoicesModal } from '../components/plan/AllInvoicesModal';
import { CancellationFooter } from '../components/plan/CancellationFooter';
import { useRevertCancellation } from '../hooks/useRevertCancellation';
import { PlanFlowModal } from '../components/plan-flow/PlanFlowModal';
import { toFlowOptions } from '../components/plan-flow/plan-flow.options';
import { needsSeatAwareChange } from '../components/plan-flow/plan-flow.items';
import { getBasePlanId } from '@/features/pricing/PlanDatas';
import { useNavigate } from 'react-router';
import { useAgencyPermissions } from '@/features/agency';
import { PlanNotices } from '../components/plan/PlanNotices';
import { PlanOffer } from '../components/plan/PlanOffer';
import { PlanTabSkeleton } from '../components/plan/PlanTabSkeleton';
import { InvoicesList } from '../components/plan/InvoicesList';
import { SubscriptionStrip } from '../components/plan/SubscriptionStrip';
import { UsageMeters } from '../components/plan/UsageMeters';
import { trialPlanOf } from '../components/plan/trial-plan';
import { buildStripFacts } from '../components/plan/plan-tab.facts';
import { TrialActivation } from '../components/plan/TrialActivation';
import { buildTrialOffer } from '../components/plan-v2/trial-offer';

const INTERACTION_PACK = 'agent-interaction';

// One flow modal replaces compare/cycle/card/cancel: those were four
// dialogs that opened over one another. 'invoices' stays its own, since it
// is a list and not a step of anything.
type OpenModal = 'none' | 'invoices' | 'plan' | 'card' | 'cancel';

/**
 * Settings → Plano.
 *
 * Ordered by what the tab is opened for: the administrative facts compressed
 * into one line, then this cycle's consumption, then — only when the numbers
 * warrant it — an offer, then invoices, then cancellation. Opening with a
 * price grid is what this replaces.
 *
 * @returns The plan tab.
 */
export function SubscriptionSettingsPage() {
  const { id: agencyId } = agencyContext.useStore((state) => state.agency);
  const { t } = useTranslation('features/pricing');
  const isMobile = useIsMobile();
  const { showSuccess, showError } = useToast();
  const [modal, setModal] = useState<OpenModal>('none');
  const [flowTarget, setFlowTarget] = useState<string | undefined>(undefined);
  const [flowCycle, setFlowCycle] = useState<'monthly' | 'yearly' | undefined>(undefined);

  /** Open the flow at a given plan and cycle, instead of at a price grid. */
  const openPlanFlow = useCallback((target?: string, cycle?: 'monthly' | 'yearly') => {
    setFlowTarget(target);
    setFlowCycle(cycle);
    setModal('plan');
  }, []);

  const {
    subscription, usage, history, invoices, invoiceTotal, plans, isLoading, isError, refetch,
    planVariants, reloadUsage,
  } = usePlanTabData(agencyId ?? '');
  // Declared with the other hooks, above the early returns.
  const { keep, isKeeping } = useRevertCancellation(agencyId ?? '', () => {
    showSuccess(t('subscription.plan.toast.kept'));
    refetch();
  });
  const { catalog, purchase } = useSubscriptionAddons(agencyId ?? '');
  // The team is what says how many of each plan's seats are in use, which is
  // what the seat controls have to show beside the counter and what the
  // who-leaves list is built from.
  const { team, reload: reloadTeam } = useAgencyTeam(agencyId ?? '');

  const meters = useMemo(() => buildMeters(usage), [usage]);
  const trialPlan = useMemo(() => trialPlanOf(usage, plans ?? [], t), [usage, plans, t]);
  // findUpgradeTarget ranks by price alone, so a yearly variant of the very
  // plan the agency is on outranks it and the invite offers an "upgrade" to
  // the current plan. Exclude the current tier by name before ranking.
  const currentName = subscription ? resolvePlanName(subscription) : '';
  // Everything that offers a change reads this list, never `plans`: the
  // catalogue also returns the tiers it only announces, and they are listed
  // with `active: false`, which is Payien's cue to refuse the change. The
  // screen used to put them on the ladder and in the flow all the same.
  const sellable = useMemo(() => (plans ?? []).filter(isPlanSelectable), [plans]);
  // The flow still needs the tier the agency is on, sellable or not: it is
  // the "current" every comparison in the modal is drawn against.
  const flowOptions = useMemo(
    () => toFlowOptions([
      ...sellable,
      ...(plans ?? []).filter((plan) => !isPlanSelectable(plan) && plan.title === currentName),
    ]),
    [sellable, plans, currentName],
  );
  // getBasePlanId, not resolvePlanTier: flowOptions.key comes from the same
  // function (toFlowOptions -> transformPlanToFeature -> getBasePlanId), and
  // resolvePlanTier answers in a different vocabulary ('emerald'/'ruby'/
  // 'diamond' instead of the catalogue's own 'essencial'/'profissional'/
  // 'premium'). The mismatch meant no plan-flow tile ever matched the
  // account's actual plan: never marked "atual", never pre-selected when
  // the flow opened, and `options.find(o => o.key === target)` quietly
  // found nothing to quote a price for.
  const currentPlanKey = getBasePlanId(
    subscription?.plan?.slug ?? subscription?.plan?.id ?? '',
  );
  const currentCycle = subscription?.billing?.billing_frequency === 'yearly' ? 'yearly' : 'monthly';
  const cardOnFile = subscription?.payment_method
    ? {
      brand: subscription.payment_method.brand,
      lastFour: subscription.payment_method.last_four,
    }
    : null;
  const priceCents = subscription?.billing?.price_cents ?? null;
  const target = useMemo(
    () => findUpgradeTarget(
      sellable.filter((plan) => plan.title !== currentName), priceCents,
    ),
    [sellable, priceCents, currentName],
  );
  // The yearly counterpart of the plan the agency is already on. Without one
  // there is nothing to switch to, and the strip omits the link rather than
  // offering a dead end.
  const cycleTwin = useMemo(
    // Read from the sellable list: the yearly variant of an announced tier
    // is announced too, so offering the cycle swap would be offering a
    // charge Payien declines.
    () => sellable.find((plan) => plan.title === currentName && plan.yearlyId) ?? null,
    [sellable, currentName],
  );
  const pack = useMemo(
    () => catalog.find((addon) => addon.slug === INTERACTION_PACK) ?? null,
    [catalog],
  );

  // One entry per plan the subscription pays for. A subscription written
  // before a team could mix tiers has none, and the block draws nothing
  // rather than inventing a single line out of `plan`.
  const items = subscription?.items ?? [];
  // Every spelling one VARIANT answers to: slug, combo key, and the
  // catalogue's own id. The lines come back in the variant's spelling — a
  // yearly subscription carries the yearly variant's ids and prices, which
  // the combined entries flatten away, so everything resolves per variant.
  const variantIdsOf = useCallback(
    (plan: (typeof planVariants)[number]) => [...new Set([
      plan.originalId, plan.value, plan.key,
      (plan as { catalogueId?: string }).catalogueId,
    ].filter(Boolean))] as string[],
    [],
  );
  const variantOf = useCallback(
    (planId: string) => planVariants.find(
      (plan) => variantIdsOf(plan).includes(planId),
    ) ?? null,
    [planVariants, variantIdsOf],
  );
  const seatPrices = useMemo(
    () => planVariants
      .filter((plan) => plan.pricePerSeat != null)
      .flatMap((plan) => variantIdsOf(plan)
        // The combo key names the tier, not the cycle: both variants share
        // it, and a price keyed by it would answer for the wrong cycle.
        .filter((planId) => planId !== plan.key)
        .map((planId) => ({
          planId,
          centsPerSeat: Math.round((plan.pricePerSeat ?? 0) * 100),
        }))),
    [planVariants, variantIdsOf],
  );
  const titleOf = useMemo(
    () => (planId: string) => variantOf(planId)?.title ?? null,
    [variantOf],
  );
  // The ladder and the pressure math speak in tiers, and the tier-stable
  // spelling both cycles share is the combo key — everything that compares
  // a line or a seat against the ladder canonicalizes to it.
  const canonicalId = useCallback(
    (planId: string) => variantOf(planId)?.key ?? planId,
    [variantOf],
  );
  const canonicalItems = useMemo(
    () => items.map((line) => ({ ...line, plan_id: canonicalId(line.plan_id) })),
    [items, canonicalId],
  );
  // What a seat of each plan gives one person, cheapest first. Both the
  // ladder and the upgrade push read it.
  const soloPlanId = canonicalItems[0]?.plan_id ?? null;
  const unlocks = useMemo(
    () => onLadder(
      seatUnlocks(
        plans
          // usePlans already filtered to this agency's segment; filtering on
          // plan.segment again only risks dropping everything when the
          // combined entry does not carry it.
          .filter((plan) => plan.limits?.seat?.interactions != null)
          .map((plan) => ({
            planId: plan.key,
            title: plan.title,
            icon: plan.icon,
            interactions: plan.limits?.seat?.interactions ?? null,
            searches: plan.limits?.seat?.partnership_searches ?? null,
            features: plan.features,
            sellable: isPlanSelectable(plan),
          }))
          .sort((a, b) => (a.interactions ?? 0) - (b.interactions ?? 0)),
        canonicalItems,
      ),
      soloPlanId,
    ),
    [plans, canonicalItems, soloPlanId],
  );
  const pressure = useMemo(
    () => mostUnderPressure(team?.people ?? []),
    [team],
  );
  // A solo broker is one seat, fixed: no adding or reducing seats, only
  // moving the single seat to another tier — which is a plan change.
  const isSolo = useIsSoloBroker();
  const navigate = useNavigate();
  const { userEmail } = useAgencyPermissions();
  // The buyer never picked which bought seat is theirs: the pick lives on
  // Settings · Team, and this tab points there while it is still open.
  const ownerSeatUnset = useMemo(() => {
    const owner = (team?.people ?? []).find((person) => person.role === 'owner');
    return Boolean(
      owner && !owner.plan_id && items.length > 0 && !isSolo
      && (owner.email || '').toLowerCase() === (userEmail || '').toLowerCase(),
    );
  }, [team, items.length, isSolo, userEmail]);
  const [pushDismissed, setPushDismissed] = useState(false);

  // A plan answers to its slug and to the catalogue's own id; which of the
  // two comes back depends on who is answering.
  const aliasesOf = useMemo(
    () => (planId: string) => {
      const match = variantOf(planId);
      return match ? variantIdsOf(match) : [];
    },
    [variantOf, variantIdsOf],
  );

  const ceilingOf = useMemo(
    () => (planId: string) => variantOf(planId)?.users ?? null,
    [variantOf],
  );

  // How many auxiliary users one seat on this plan brings. Zero for a plan
  // the catalogue does not declare it on, which is what keeps the preview
  // quiet instead of promising places nobody sells.
  const supportOf = useMemo(
    () => (planId: string) => variantOf(planId)?.limits?.seat?.support_seats ?? 0,
    [variantOf],
  );

  // Above the early returns: a hook after one of them runs on some renders
  // and not others, and React counts hooks. Placed below, it took the whole
  // tab down with "Rendered more hooks than during the previous render".
  const stacked = useMediaQuery('(max-width: 768px)');

  if (!agencyId) return null;
  if (isLoading) return <PlanTabSkeleton />;

  if (isError || !subscription) {
    return (
      <div className="flex flex-col gap-(--space-2)" data-testid="plan-tab-error">
        <Text component="span" style={{ fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)' }}>
          {t('subscription.plan.error.loadFailed')}
        </Text>
        <UnstyledButton onClick={refetch} style={{
          fontSize: 'var(--text-meta)', fontWeight: 'var(--font-semibold)',
          color: 'var(--seed-base)', textDecoration: 'underline',
          textUnderlineOffset: '3px', alignSelf: 'flex-start',
        }}>
          {t('subscription.plan.error.retry')}
        </UnstyledButton>
      </div>
    );
  }

  const offer = decideOffer({
    meters,
    hasUpgradeTarget: target !== null,
    packAvailable: FF_APP_ADDONS && pack !== null,
    subscriptionStatus: subscription.subscription_status,
  });

  const buyPack = () => {
    if (!pack) return;
    purchase(pack.slug)
      .then(() => showSuccess(t('subscription.plan.toast.packPurchased')))
      .catch(() => showError(t('subscription.plan.toast.packFailed')));
  };

  // The renewal total already counts a scheduled reduction, so the two
  // figures below never disagree with the seats block above them. It also
  // counts the coupon Payien is still honouring: quoted from the catalogue
  // alone, an agency two cycles into a coupon was shown the full price for a
  // renewal that is going to be discounted.
  const scheduled = subscription?.scheduled_items_change ?? null;
  const priceOfSeat = (planId: string) => (
    seatPrices.find((price) => price.planId === planId)?.centsPerSeat ?? 0
  );
  const renewalBaseCents = cycleTotalCents(scheduled?.items ?? items ?? [], priceOfSeat);
  const renewalCents = nextChargeCents(
    items, scheduled, priceOfSeat, subscription?.active_discount,
  );
  // The charges block says an amount moved because the team changed size, so
  // it needs the two team sizes and not just the two amounts. Seats only
  // change at renewal, through a scheduled change, so today's count is the
  // one the last charge covered.
  const seatsNow = seatCount(items);
  const seatsNext = seatsAtRenewal(items, scheduled);

  const isCancelled = subscription.subscription_status === 'cancelled'
    || Boolean(subscription.cancelled_at);
  // The owner asked to cancel and the access runs to the renewal date: the
  // status has not changed, so this is the only thing that says it (BUG-048).
  const cancelScheduled = Boolean(subscription.cancel_at_period_end) && !isCancelled;
  const renewalDate = formatLocalizedDate(subscription.next_renewal_date);
  // Null unless the subscription is trialing (RN-10); every trial-only
  // wording below hangs on it, so a subscription that is not in trial reads
  // exactly as before.
  const activation = buildTrialOffer(subscription, {
    titleOf,
    seatLimitsOf: (planId) => variantOf(planId)?.limits?.seat,
    soloName: resolvePlanName(subscription),
    renewalCents, priceCents,
  });
  const trialing = subscription.subscription_status === 'trialing';
  // Payien moves a team's cycle only in the free period (BUG-043); once it is
  // paid the link to switch is not drawn.
  const cycleSwitchable = trialing || !needsSeatAwareChange(subscription.items);

  return (
    <div
      className="flex flex-col gap-(--space-5)"
      data-testid="plan-tab"
      style={{
        // On a phone this tab opens inside a full-screen overlay that carries
        // no padding of its own -- each section provides it, and this one
        // never did. Six blocks that are not cards (the stale-data note, the
        // ladder's title, the invoices list and the cancellation footer) ran
        // flush against the edge of the screen. Zero above, where the shell
        // already insets the column.
        paddingLeft: stacked ? 'var(--space-4)' : 0,
        paddingRight: stacked ? 'var(--space-4)' : 0,
      }}
    >
      {activation && (
        <TrialActivation
          agencyId={agencyId}
          offer={activation}
          onReload={() => { refetch(); reloadUsage(); }}
          onChangeCard={() => setModal('card')}
        />
      )}

      {items.length > 0 ? (
        <PlanSummary
          segmentLabel={t(isSolo
            ? 'subscription.plan.summary.solo'
            : 'subscription.plan.summary.agency')}
          items={items}
          scheduled={subscription?.scheduled_items_change ?? null}
          priceOf={(planId) => (
            seatPrices.find((price) => price.planId === planId)?.centsPerSeat ?? 0
          )}
          titleOf={titleOf}
          billingFrequency={subscription?.billing?.billing_frequency ?? null}
          nextChargeDate={subscription?.next_renewal_date ?? null}
          cardBrand={subscription?.payment_method?.brand ?? null}
          cardLastFour={subscription?.payment_method?.last_four ?? null}
          onChangeCard={() => setModal('card')}
          onSwitchToYearly={cycleSwitchable ? () => openPlanFlow(undefined, 'yearly') : undefined}
          trial={trialing}
          cancelScheduled={cancelScheduled}
        />
      ) : (
        <SubscriptionStrip
          planName={resolvePlanName(subscription) || t('subscription.plan.strip.planUnknown')}
          price={priceCents === null
            ? t('subscription.plan.strip.priceUnknown')
            : `${formatCents(priceCents)}${t('subscription.plan.strip.perMonth')}`}
          facts={buildStripFacts(subscription, {
            onChangeCycle: cycleTwin ? () => openPlanFlow(undefined, 'yearly') : undefined,
            onChangeCard: () => setModal('card'),
          }, t)}
        />
      )}

      <PlanNotices subscription={subscription} />

      {ownerSeatUnset && (
        <UnstyledButton
          data-testid="owner-seat-unset-notice"
          onClick={() => navigate('../settings')}
          style={{
            padding: 'var(--space-2-5) var(--space-3-5)', textAlign: 'left',
            borderRadius: 'var(--radius-lg)', background: 'var(--surface-raised)',
            border: '1px solid var(--border-subtle)', cursor: 'pointer',
            fontSize: 'var(--text-small)', lineHeight: 1.4,
            color: 'var(--text-primary)',
          }}
        >
          {t('subscription.plan.ownerSeatUnset')}
          {' '}
          <Text
            component="span"
            style={{
              fontWeight: 'var(--font-bold)', color: 'var(--seed-base)',
              textDecoration: 'underline', textUnderlineOffset: '3px',
            }}
          >
            {t('subscription.plan.ownerSeatUnsetCta')}
          </Text>
        </UnstyledButton>
      )}

      {pressure && !pushDismissed && (() => {
        const offer = upgradeOffer(
          pressure && {
            ...pressure,
            person: {
              ...pressure.person,
              plan_id: pressure.person.plan_id
                ? canonicalId(pressure.person.plan_id)
                : pressure.person.plan_id,
            },
          },
          unlocks,
          team?.seats.by_plan?.map((block) => ({
            ...block, plan_id: canonicalId(block.plan_id),
          })),
        );
        if (!offer) return null;
        return (
          <UpgradePush
            pressure={pressure}
            current={offer.current}
            target={offer.target}
            seatFreeOnTarget={offer.seatFreeOnTarget}
            onAct={() => openPlanFlow(target?.key)}
            onDismiss={() => setPushDismissed(true)}
          />
        );
      })()}

      {items.length > 0 && !isSolo && (
        <SeatsBlock
          agencyId={agencyId}
          team={team}
          items={items}
          prices={seatPrices}
          planTitle={titleOf}
          maxSeats={ceilingOf}
          planAliases={aliasesOf}
          supportPerSeat={supportOf}
          nextRenewalDate={subscription?.next_renewal_date ?? null}
          billingCycleDays={subscription?.billing?.billing_cycle_days ?? 30}
          onChanged={() => { refetch(); reloadTeam(); }}
        />
      )}

      {!isSolo && (
        <SupportSeatsMeter
          supportSeats={team?.support_seats}
          tiers={items
            .map((line) => ({
              planId: line.plan_id,
              title: titleOf(line.plan_id) ?? line.plan_id,
              seats: line.quantity,
              perSeat: supportOf(line.plan_id),
            }))
            .filter((tier) => tier.perSeat > 0 && tier.seats > 0)}
          onAddSeat={() => {
            document
              .querySelector('[data-testid="plan-seats"]')
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        />
      )}

      {/* The single-plan screen, for a subscription with no lines — which is
          every solo broker, and every agency written before a team could mix
          tiers. On a subscription made of lines these two say the same things
          the summary, the seats block and the ladder say, in the shape of a
          subscription that is one tier for everybody: a usage card for
          whoever is looking, and an offer ranked on one price.

          For a team, the allowances are per person and live on Settings ·
          Team, where each row shows the ceiling of the seat it sits in. */}
      {items.length === 0 && (
        <>
          <UsageMeters
            meters={meters} renewsOn={renewalDate} compact={isMobile}
            trialPlan={trialPlan}
          />

          <PlanOffer
            offer={offer}
            meters={meters}
            history={history}
            target={target}
            currentPriceCents={priceCents}
            pack={pack}
            onBuyPack={buyPack}
            onCompare={() => openPlanFlow(target?.key)}
          />
        </>
      )}

      {unlocks.length > 0 && (
        <SeatUnlocks
          unlocks={unlocks}
          solo={isSolo}
          currentPlanId={isSolo ? soloPlanId : null}
          onAdd={(planId) => {
            // The card names a plan, so the flow opens on that plan: opening
            // with no target fell back to the current one, and the buyer had
            // to pick again what the click already said.
            if (isSolo) {
              openPlanFlow(planId);
              return;
            }
            document.querySelector('[data-testid="plan-seats"]')
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        />
      )}

      <BillingDates
        last={lastCharge(invoices, seatsNow)}
        // A coupon that zeroes the cycle is still a charge on a date; the
        // seats pricing at nothing is what means there is no renewal to quote.
        next={cancelScheduled ? null : nextCharge(
          subscription?.next_renewal_date, renewalCents, seatsNext,
          renewalBaseCents > 0,
        )}
        reduced={Boolean(scheduled?.items?.length)}
        trial={trialing}
        onSeeInvoices={() => setModal('invoices')}
      />

      <InvoicesList
        invoices={invoices}
        total={invoiceTotal}
        onSeeAll={() => setModal('invoices')}
      />

      <CancellationFooter
        renewalDate={renewalDate}
        isCancelled={isCancelled}
        trialing={trialing}
        scheduled={cancelScheduled}
        onKeep={isKeeping ? undefined : keep}
        onCancel={() => setModal('cancel')}
        onReactivate={() => openPlanFlow()}
      />

      <AllInvoicesModal
        agencyId={agencyId}
        opened={modal === 'invoices'}
        onClose={() => setModal('none')}
      />
      {/* Mounted only for the three modes it serves. Kept mounted while
          closed, `mode` was 'none' or 'invoices' — values PlanFlowMode does
          not have — and the body ran anyway, since the modal has no early
          return on `opened`: copyKeysFor('none') answered undefined and the
          tab died on `copy.confirm`. The guard narrows `modal` to exactly
          PlanFlowMode, so `mode` needs no cast to type-check. */}
      {(modal === 'plan' || modal === 'card' || modal === 'cancel') && (
        <PlanFlowModal
          agencyId={agencyId ?? ''}
          opened
          mode={modal}
          options={flowOptions}
          currentKey={currentPlanKey}
          currentCycle={currentCycle}
          renewalDate={renewalDate ?? ''}
          card={cardOnFile}
          initialTarget={flowTarget}
          initialCycle={flowCycle}
          items={subscription?.items}
          trialing={subscription?.subscription_status === 'trialing'}
          nextChargeCents={renewalCents}
          onClose={() => setModal('none')}
          onChanged={(message) => {
            showSuccess(message);
            refetch();
          }}
        />
      )}
    </div>
  );
}

export default SubscriptionSettingsPage;
